import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { type RemovableRef, useLocalStorage } from '@vueuse/core'
import { merge } from 'lodash'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { useApiClient } from '@/api'
import { USER_ROLES } from '@/constants/constants'
import dayjs from '@/dayjs'
import {
  CUSTOM_EVENTS,
  logEvent,
  resetAnalyticsCache,
  setupAnalyticsSession,
} from '@/firebase-analytics'
import type {
  CustomTokenRefresh,
  FeatureResponse,
  InvitationRetrieveToken,
  Login,
  LoginResponse,
  Logout,
  LongLivedTokenRefresh,
  NullEnum,
  PasscodeNotificationChannelEnum,
  PatchedUser,
  ProfileReadOnly,
  ProfileTypeEnum,
  SendVerificationCode,
  UpdatePassword,
} from '@/generated/api'
import i18n from '@/i18n'
import { STORAGE_KEYS, TOKEN_REFRESH_LEEWAY_SECONDS } from '@/settings'
import { getTokenExpiration, isTokenExpired, RefreshExpiredError } from '@/utils/jwt'

import { useCounterStore } from '../counter/store'
import { useSurveyStore } from '../survey/store'

type TokenInfo = {
  access?: string
  refresh?: string
  profile_type?: ProfileTypeEnum
}

type TokenResponse = LoginResponse | CustomTokenRefresh

/**
 * Helper functions for device token management via Capacitor Preferences
 */
async function getDeviceToken(): Promise<string> {
  const { value } = await Preferences.get({ key: STORAGE_KEYS.DEVICE_TOKEN })
  return value || ''
}

async function setDeviceToken(token: string): Promise<void> {
  await Preferences.set({ key: STORAGE_KEYS.DEVICE_TOKEN, value: token })
}

async function removeDeviceToken(): Promise<void> {
  await Preferences.remove({ key: STORAGE_KEYS.DEVICE_TOKEN })
}

/**
 * Validates that a token is not null, undefined, or empty string
 * Tokens should be either null/undefined (not set) or a valid non-empty string
 */
function isValidToken(token: string | null | undefined): boolean {
  return typeof token === 'string' && token.trim() !== ''
}

/**
 * Helper functions for managing access token expiration timestamp
 */
async function getAccessExpTs(): Promise<number | null> {
  const { value } = await Preferences.get({ key: STORAGE_KEYS.ACCESS_EXP_TS })
  return value ? parseInt(value, 10) : null
}

async function setAccessExpTs(accessToken: string): Promise<void> {
  try {
    const accessExp = getTokenExpiration(accessToken)
    if (accessExp) {
      const value = accessExp.toString()
      await Preferences.set({ key: STORAGE_KEYS.ACCESS_EXP_TS, value })
    }
  } catch (error) {
    console.error('[Auth] Failed to decode access token expiration:', error)
  }
}

async function removeAccessExpTs(): Promise<void> {
  await Preferences.remove({ key: STORAGE_KEYS.ACCESS_EXP_TS })
}

export const useAuthStore = defineStore('auth', () => {
  const fcmToken = useLocalStorage(STORAGE_KEYS.FCM_TOKEN, '', { mergeDefaults: true })
  const apiClient = useApiClient()
  const user = ref(null as ProfileReadOnly | null)
  const onboardingUserInfo = ref(null as InvitationRetrieveToken | null)
  const token = Capacitor.isNativePlatform()
    ? ref({} as TokenInfo)
    : useLocalStorage(STORAGE_KEYS.AUTH_TOKEN, {} as TokenInfo, { mergeDefaults: true })
  const counterStore = useCounterStore()

  const features: RemovableRef<FeatureResponse> = useLocalStorage(
    STORAGE_KEYS.FEATURES,
    { analytics: false },
    { mergeDefaults: true }
  )

  // Load long-lived token asynchronously for all platforms
  Preferences.get({ key: STORAGE_KEYS.LONG_LIVED_TOKEN }).then(async ({ value }) => {
    if (value) {
      try {
        const loadedToken = JSON.parse(value)
        // Validate tokens when loading from Preferences
        // If tokens are valid, use them (overrides regular token for long-lived mode)
        if (isValidToken(loadedToken.access) && isValidToken(loadedToken.refresh)) {
          token.value = loadedToken
          // Decode and store access token expiration timestamp
          await setAccessExpTs(loadedToken.access)
        } else {
          // Invalid tokens - clear them
          await Preferences.remove({ key: STORAGE_KEYS.LONG_LIVED_TOKEN })
        }
      } catch (error) {
        console.error('[Auth] Failed to parse long-lived token:', error)
        await Preferences.remove({ key: STORAGE_KEYS.LONG_LIVED_TOKEN })
      }
    }
  })

  // Race condition protection for logout and refresh
  const isLoggingOut = ref(false)
  const isRefreshing = ref(false)

  let refreshPromise: Promise<TokenResponse | void> | null = null

  // Clear all tokens and related data
  async function clearTokens(): Promise<void> {
    token.value = {}
    await removeAccessExpTs()
    await Preferences.remove({ key: STORAGE_KEYS.LONG_LIVED_TOKEN })
  }

  async function getFeatures(force = false): Promise<FeatureResponse> {
    if (force || !features.value || !Object.keys(features.value).length) {
      features.value = await apiClient.features.featuresRetrieve()
    }
    return features.value
  }

  async function fetchOnboardingUserInfoBeforeLogin(
    token: string
  ): Promise<InvitationRetrieveToken | null> {
    if (onboardingUserInfo.value) {
      return onboardingUserInfo.value
    }
    onboardingUserInfo.value = await apiClient.invitation.invitationRetrieve({ token })

    i18n.global.locale.value =
      user.value?.user.app_language ?? onboardingUserInfo.value.language ?? i18n.global.locale.value
    dayjs.locale(i18n.global.locale.value)
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, i18n.global.locale.value)

    return onboardingUserInfo.value
  }

  async function fetchMe(force = false): Promise<ProfileReadOnly | null> {
    if (user.value && !force) {
      return user.value
    }
    // Validate refresh token before making request
    if (!isValidToken(token.value.refresh)) {
      return null
    }
    user.value = await apiClient.me.meRetrieve()
    // Fetch features for all user types to ensure analytics and other feature flags work correctly
    await getFeatures(true)

    token.value.profile_type = user.value?.type

    // by default, we should use user's language that we get from INES
    i18n.global.locale.value = user.value?.user.app_language || i18n.global.locale.value
    dayjs.locale(i18n.global.locale.value)
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, i18n.global.locale.value)

    return user.value
  }

  async function updateMe(options: PatchedUser): Promise<void> {
    const baseUser = await apiClient.me.meUpdatePartialUpdate({
      requestBody: options,
    })
    user.value = merge(user.value, { user: baseUser })
  }

  async function updateMePassword(passwords: UpdatePassword): Promise<void> {
    await apiClient.me.meUpdatePasswordUpdate({
      requestBody: passwords,
    })
  }

  async function sendPushNotificationsToken(token: string) {
    await apiClient.fcmtoken.fcmtokenCreate({ requestBody: { token } })
    fcmToken.value = token
  }

  async function sendStartLiveActivityToken(fcmToken: string, startToken: string) {
    try {
      await apiClient.fcmtoken.fcmtokenPushToStartPartialUpdate({
        requestBody: { token: fcmToken, push_to_start_token: startToken },
      })
      console.log('[Live Activity] Successfully sent start token')
    } catch (error) {
      console.error('[Live Activity] Failed to send start token:', error)
      throw error
    }
  }

  async function removePushNotificationsToken(token: string) {
    await apiClient.fcmtoken.fcmtokenDestroy({ token })
  }

  async function checkCredentialsForCode(
    email: string,
    password: string,
    phone?: string,
    channel?: PasscodeNotificationChannelEnum | '' | null | NullEnum
  ): Promise<SendVerificationCode> {
    const requestBody = { email, password } as SendVerificationCode

    if (Capacitor.isNativePlatform()) {
      requestBody.device_token = await getDeviceToken()
    }
    if (phone) {
      requestBody.default_phone_number = phone
    }
    if (channel) {
      requestBody.passcode_notification_channel = channel
    }
    return apiClient.fa.faSendVerificationCodeCreate({
      requestBody,
    })
  }

  async function login(email: string, password: string, code?: string) {
    // to be sure that token access is valid and not expired let's logout before login
    await logout(true)
    let requestBody: Login = { email, password }
    if (code) {
      requestBody = { ...requestBody, code }
    }
    if (Capacitor.isNativePlatform()) {
      requestBody.device_token = await getDeviceToken()
    }
    const result = await apiClient.login.loginCreate({
      requestBody,
      isNewVersion: true,
    })
    if (Capacitor.isNativePlatform() && result.device_token) {
      await setDeviceToken(result.device_token)
    }
    const access = result.access
    const refresh = result.refresh
    // Validate that tokens are not empty strings before storing
    if (isValidToken(access) && isValidToken(refresh)) {
      token.value = {
        access: access!,
        refresh: refresh!,
      }

      // Decode and store access token expiration timestamp
      await setAccessExpTs(access!)

      // Set session start time for absolute session limit
      await Preferences.set({ key: STORAGE_KEYS.SESSION_START, value: Date.now().toString() })
    } else {
      // Clear tokens if they are invalid
      await clearTokens()
    }
    const user = await fetchMe()
    if (user?.type === USER_ROLES.PATIENT) {
      const surveyStore = useSurveyStore()
      surveyStore.clearPractitionerData()
    }
    if (Capacitor.isNativePlatform() && fcmToken.value) {
      await sendPushNotificationsToken(fcmToken.value)
    }

    // Setup analytics session and log login event (fire and forget)
    setupAnalyticsSession().then(() => {
      logEvent(CUSTOM_EVENTS.LOGIN, {})
    })

    return user
  }

  async function logout(keepDeviceToken?: boolean) {
    console.log('[Auth] Logging out with keepDeviceToken:', keepDeviceToken)
    // Prevent infinite logout loop - if already logging out, just return
    if (isLoggingOut.value) {
      return
    }

    // Get valid access token before setting isLoggingOut flag (will refresh if expired)
    // IMPORTANT: Must get token BEFORE setting isLoggingOut, otherwise getAccessToken() will throw error
    // This ensures logout request succeeds and token gets blacklisted on backend
    let accessTokenToUse: string | null = null
    try {
      accessTokenToUse = await getAccessToken()
    } catch (error) {
      // If refresh token expired, continue logout locally without backend request
      if (error instanceof RefreshExpiredError) {
        console.log('[Auth] Refresh token expired during logout, continuing locally')
        accessTokenToUse = null
      } else {
        throw error
      }
    }

    isLoggingOut.value = true

    try {
      // Save refresh token to send in logout request body
      const refreshTokenToInvalidate = getRefreshToken()!

      // Clear user data and app state
      user.value = null
      onboardingUserInfo.value = null
      counterStore.counter = null
      features.value = { analytics: false }
      resetAnalyticsCache()

      // Send logout request to backend to blacklist the refresh token
      // API client gets access token from token.value (refresh prevented by isLoggingOut flag)
      if (refreshTokenToInvalidate && accessTokenToUse) {
        const requestBody: Logout = { refresh: refreshTokenToInvalidate }

        // Device token for web is managed by cookies, so we don't need to send it
        if (Capacitor.isNativePlatform()) {
          requestBody.device_token = await getDeviceToken()
        }
        if (fcmToken.value) {
          requestBody.fcm_token = fcmToken.value
        }

        // Add keep_device_token flag for inactivity logout
        if (keepDeviceToken) {
          requestBody.keep_device_token = true
        }

        // Ignore errors from logout request - token might already be blacklisted
        await apiClient.logout
          .logoutCreate({ requestBody })
          .catch((e) => {
            // Log but don't throw - token might already be blacklisted or invalid
            console.log('Logout request failed (expected if token already blacklisted):', e)
          })
          .finally(async () => {
            // Clean up tokens and device token after logout request completes
            if (!keepDeviceToken) {
              await removeDeviceToken()
            }
            await clearTokens()
          })
      } else {
        // If logout request can't be sent (no tokens), clean up locally
        await clearTokens()
        if (!keepDeviceToken) {
          await removeDeviceToken()
        }
      }

      // Clear Live Activity and session tracking data
      localStorage.removeItem(STORAGE_KEYS.START_LIVE_ACTIVITY_TOKEN)
      await Preferences.remove({ key: STORAGE_KEYS.SESSION_START })
    } finally {
      isLoggingOut.value = false
    }
  }

  async function removeFcmToken() {
    if (Capacitor.isNativePlatform() && fcmToken.value) {
      await removePushNotificationsToken(fcmToken.value)
    }
    fcmToken.value = ''
  }

  async function storeToken(access: string | null, refresh: string | null, long?: boolean) {
    // Only store tokens if both are valid (not null, undefined, or empty strings)
    if (isValidToken(access) && isValidToken(refresh)) {
      // Preserve existing profile_type when updating tokens
      const currentProfileType = token.value.profile_type
      token.value = {
        access: access!,
        refresh: refresh!,
        profile_type: currentProfileType,
      }

      // Decode and store access token expiration timestamp
      await setAccessExpTs(access!)

      if (long) {
        await Preferences.set({
          key: STORAGE_KEYS.LONG_LIVED_TOKEN,
          value: JSON.stringify(token.value),
        })
      }
    } else {
      // If tokens are invalid, clear them
      await clearTokens()
    }
  }

  // Internal function that performs the actual token refresh
  // Unified function for both regular and long-lived token refresh
  async function doRefreshAccessToken(
    isLongLived: boolean,
    currentRefreshToken?: string
  ): Promise<TokenResponse | void> {
    // Remove long-lived token only for regular refresh (not long-lived)
    if (!isLongLived) {
      await Preferences.remove({ key: STORAGE_KEYS.LONG_LIVED_TOKEN })
    }

    // Use current refresh token from store if not provided, but validate it
    const refreshTokenToUse = currentRefreshToken || token.value.refresh

    // Validate refresh token before making request
    if (!isValidToken(refreshTokenToUse)) {
      await clearTokens()
      return
    }

    // Check if refresh token has expired (with leeway)
    if (isTokenExpired(refreshTokenToUse!, TOKEN_REFRESH_LEEWAY_SECONDS)) {
      const errorMessage = isLongLived
        ? 'Long-lived refresh token has expired'
        : 'Refresh token has expired'
      console.warn(`[Auth] ${errorMessage}`)
      await clearTokens()
      throw new RefreshExpiredError(errorMessage)
    }

    try {
      // Call appropriate API endpoint based on refresh type
      const result = isLongLived
        ? await apiClient.login.loginRefreshLongLivedCreate({
            requestBody: { refresh: refreshTokenToUse } as LongLivedTokenRefresh,
          })
        : await apiClient.login.loginRefreshCreate({
            requestBody: {
              refresh: refreshTokenToUse,
              profile_type: token.value.profile_type,
            } as CustomTokenRefresh,
          })

      // Validate tokens from response before storing
      // Backend should always return valid tokens if request was successful
      if (isValidToken(result.access) && isValidToken(result.refresh)) {
        await storeToken(result.access, result.refresh, isLongLived)
        return result
      } else {
        // If tokens are invalid (shouldn't happen, but handle it anyway)
        const endpointName = isLongLived ? 'refresh-long-lived endpoint' : 'refresh endpoint'
        await clearTokens()
        throw new Error(`Invalid tokens received from ${endpointName}`)
      }
    } catch (error) {
      // On error, clear tokens since refresh failed
      await clearTokens()
      throw error
    }
  }

  // Unified refresh function with race condition protection
  // Handles both regular and long-lived token refresh
  async function refreshAccessToken(
    isLongLived: boolean,
    currentRefreshToken?: string
  ): Promise<TokenResponse | void> {
    if (isLoggingOut.value) {
      console.log(`[Auth] Logout in progress, skipping ${isLongLived ? 'long-lived ' : ''}refresh`)
      return
    }

    // If already refreshing (any type), return existing promise
    if (isRefreshing.value && refreshPromise) {
      console.log('[Auth] Refresh already in progress, reusing promise')
      return refreshPromise
    }

    console.log(`[Auth] Starting new ${isLongLived ? 'long-lived ' : ''}token refresh`)
    isRefreshing.value = true

    refreshPromise = doRefreshAccessToken(isLongLived, currentRefreshToken)
      .catch(async (error) => {
        // For RefreshExpiredError, re-throw it so App.vue interceptor can handle it
        // This ensures toast message is shown to user consistently
        if (error instanceof RefreshExpiredError) {
          console.log(
            `[Auth] Refresh token expired in refreshAccessToken(${isLongLived}), re-throwing for interceptor`
          )
          // Clear tokens before re-throwing
          await clearTokens()
          throw error
        }
        // Re-throw other errors
        throw error
      })
      .finally(() => {
        console.log(`[Auth] ${isLongLived ? 'Long-lived ' : ''}Refresh completed`)
        isRefreshing.value = false
        refreshPromise = null
      })

    return refreshPromise
  }

  // Determines which type of refresh to use (regular or long-lived)
  // Returns true for long-lived refresh, false for regular refresh
  async function determineRefreshType(): Promise<boolean> {
    // Check if we have a long-lived token stored in Preferences
    // If it exists, we should continue using long-lived refresh
    const { value: longLivedTokenValue } = await Preferences.get({
      key: STORAGE_KEYS.LONG_LIVED_TOKEN,
    })
    const hasLongLivedToken = longLivedTokenValue !== null && longLivedTokenValue !== ''

    // Determine if we need long-lived refresh
    const surveyStore = useSurveyStore()
    const surveyData = surveyStore.practitionerData
    const isPractitioner = token.value.profile_type === USER_ROLES.PRACTITIONER

    return hasLongLivedToken || (isPractitioner && surveyData?.patientId)
  }

  // Get refresh token safely - validates token exists and is valid
  function getRefreshToken(): string | null {
    const refresh = token.value.refresh
    if (!isValidToken(refresh)) {
      return null
    }
    return refresh || null
  }

  async function getAccessToken(forceRefresh = false): Promise<string> {
    const { access, refresh: refreshToken } = token.value
    // Check that access token exists, is not empty string, and refresh token is also valid
    if (!isValidToken(access) || !isValidToken(refreshToken)) {
      throw new RefreshExpiredError('Refresh token has expired')
    }

    // Determine if we need to refresh
    const needsRefresh = forceRefresh || (await shouldRefreshProactively())

    if (needsRefresh) {
      // If logout is in progress, return current token without refreshing
      // This allows logout request to use the existing token
      if (isLoggingOut.value) {
        console.log('[Auth] Logout in progress, returning current token without refresh')
        return access!
      }

      console.log(`[Auth] Refreshing token (forceRefresh: ${forceRefresh})`)

      try {
        const isLongLived = await determineRefreshType()
        await refreshAccessToken(isLongLived)

        // Return the new access token after refresh
        // Validate token after refresh to prevent returning empty string
        if (!isValidToken(token.value.access)) {
          throw new RefreshExpiredError('Access token is invalid after refresh')
        }
        return token.value.access!
      } catch (error) {
        // If refresh token expired, throw RefreshExpiredError to trigger logout
        if (error instanceof RefreshExpiredError) {
          console.error(`[Auth] Refresh token expired (forceRefresh: ${forceRefresh})`)
          throw error
        }
        console.error(`[Auth] Failed to refresh token (forceRefresh: ${forceRefresh}):`, error)
        // Validate token before returning to prevent returning empty string
        // If token is invalid, throw error instead of returning empty string
        if (!isValidToken(access)) {
          throw new RefreshExpiredError('Access token is invalid after failed refresh')
        }
        // Return current token and let the API request fail with 401
        // which will trigger the interceptor refresh logic
        return access!
      }
    }

    // Token is valid and doesn't need refresh - return it
    // This works even during logout, allowing logout request to use the existing valid token
    return access!
  }

  // Helper function to check if proactive refresh is needed
  async function shouldRefreshProactively(): Promise<boolean> {
    const accessExpTs = await getAccessExpTs()
    if (!accessExpTs) {
      return false
    }
    const nowSeconds = Math.floor(Date.now() / 1000)
    return nowSeconds >= accessExpTs - TOKEN_REFRESH_LEEWAY_SECONDS
  }

  async function setNewPassword(password: string, token: string) {
    await apiClient.setPassword.setPasswordCreate({
      requestBody: { token, password },
    })
  }

  async function resetPassword(email: string) {
    await apiClient.resetPassword.resetPasswordCreate({
      requestBody: { email },
    })
  }

  return {
    fcmToken,
    user,
    onboardingUserInfo,
    isLoggingOut,
    isRefreshing,
    fetchOnboardingUserInfoBeforeLogin,
    fetchMe,
    updateMe,
    updateMePassword,
    login,
    logout,
    getAccessToken,
    getRefreshToken,
    refreshAccessToken,
    setNewPassword,
    resetPassword,
    sendPushNotificationsToken,
    sendStartLiveActivityToken,
    removeFcmToken,
    checkCredentialsForCode,
    getFeatures,
    features,
  }
})
